import { MONS } from '../mock/data.ts';
import type { SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

type Suggestion = {
  species: string;
  count: number;
  fraction: number;
  mega_form?: string | null;
  mega_count?: number | null;
};

type Result = {
  suggestions: Suggestion[];
  teams_matched: number;
  teams_considered: number;
};

// Output of get_build_suggestions. Ranked partner species that co-occur
// with the user's anchor picks across tournament teams. Co-occurrence-only
// signal — surface as candidate fillers, not as a "this is the team" claim.
export function BuildSuggestionsCard({ data }: { data: Result }) {
  if (!data || !Array.isArray(data.suggestions) || data.suggestions.length === 0) {
    return (
      <div
        className="mono"
        style={{
          padding: '12px 14px',
          border: '1px dashed var(--line-2)',
          borderRadius: 4,
          color: 'var(--fg-3)',
          fontSize: 12,
          textAlign: 'center',
        }}
      >
        no co-occurrence data for those anchors yet
      </div>
    );
  }
  return (
    <div className="vb-card">
      <div className="vb-card-header">
        <span
          className="caps"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          partner candidates
        </span>
        <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
          {data.teams_matched} teams contain all anchors
        </span>
      </div>
      <div>
        {data.suggestions.map((s, i) => (
          <div
            key={s.species}
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr auto auto',
              gap: 10,
              alignItems: 'center',
              padding: '6px 12px',
              borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            }}
          >
            <Sprite
              species={(s.mega_form ? s.mega_form.toLowerCase() : s.species) as SpeciesId}
              size={28}
            />
            <div
              style={{
                color: 'var(--fg-1)',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>{s.mega_form ?? MONS[s.species]?.name ?? s.species}</span>
              {s.mega_form && s.mega_count ? (
                <span
                  className="caps"
                  style={{
                    fontSize: 9,
                    padding: '1px 5px',
                    color: 'var(--accent-2)',
                    background: 'var(--accent-bg)',
                    border: '1px solid var(--accent-line)',
                    borderRadius: 2,
                    letterSpacing: '0.06em',
                  }}
                  title={`Held a mega stone in ${s.mega_count} of ${data.teams_matched} matched teams`}
                >
                  MEGA · {s.mega_count}/{data.teams_matched}
                </span>
              ) : null}
            </div>
            <div className="ev-bar" style={{ width: 80 }}>
              <div className="fill" style={{ width: `${Math.min(100, s.fraction * 100)}%` }} />
            </div>
            <div className="mono tabular" style={{ color: 'var(--fg-1)', fontSize: 12 }}>
              {(s.fraction * 100).toFixed(0)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
